/**
 * useRadioCallinHost — host-side call-in signaling + peer connections.
 *
 * Usage (inside RadioHostStudio):
 *   const callin = useRadioCallinHost({
 *     slug: "billboard-hot-100",
 *     enabled: callinOpen,
 *     attachCallerStream: (callerId, stream) => { engine.attachCallerStream(stream) }
 *   });
 *
 *   callin.callers // [{ caller_id, pc, stream }]
 *   callin.kick(callerId)
 *
 * Signaling over WS:
 *   { type: "joined", caller_id }
 *   { type: "offer"|"ice", caller_id, sdp|candidate }
 *   { type: "answer", caller_id, sdp }
 *   { type: "left", caller_id }
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "./api";

export default function useRadioCallinHost({ slug, enabled, token, onCallerAudio }) {
  const [callers, setCallers] = useState({}); // { caller_id: { pc, stream } }
  const wsRef = useRef(null);
  const peersRef = useRef({}); // caller_id -> RTCPeerConnection
  const iceServersRef = useRef(null);

  const ensureIce = useCallback(async () => {
    if (iceServersRef.current) return iceServersRef.current;
    try {
      const { data } = await api.get("/webrtc/ice-servers");
      iceServersRef.current = data?.iceServers || [{ urls: "stun:stun.l.google.com:19302" }];
    } catch {
      iceServersRef.current = [{ urls: "stun:stun.l.google.com:19302" }];
    }
    return iceServersRef.current;
  }, []);

  const cleanupPeer = (callerId) => {
    const p = peersRef.current[callerId];
    if (p) {
      try { p.close(); } catch {}
      delete peersRef.current[callerId];
    }
    setCallers((prev) => {
      const n = { ...prev };
      delete n[callerId];
      return n;
    });
  };

  const handleOffer = useCallback(async ({ caller_id, sdp }) => {
    const iceServers = await ensureIce();
    let pc = peersRef.current[caller_id];
    if (!pc) {
      pc = new RTCPeerConnection({ iceServers });
      peersRef.current[caller_id] = pc;
      pc.ontrack = (ev) => {
        const [stream] = ev.streams;
        if (!stream) return;
        setCallers((prev) => ({ ...prev, [caller_id]: { ...(prev[caller_id] || {}), stream } }));
        try { onCallerAudio?.(caller_id, stream); } catch {}
      };
      pc.onicecandidate = (ev) => {
        if (ev.candidate && wsRef.current && wsRef.current.readyState === 1) {
          wsRef.current.send(JSON.stringify({ type: "ice", caller_id, candidate: ev.candidate }));
        }
      };
      pc.onconnectionstatechange = () => {
        const s = pc.connectionState;
        if (s === "failed" || s === "closed" || s === "disconnected") cleanupPeer(caller_id);
      };
    }
    await pc.setRemoteDescription({ type: "offer", sdp });
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    if (wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify({ type: "answer", caller_id, sdp: pc.localDescription.sdp }));
    }
  }, [ensureIce, onCallerAudio]);

  const handleIce = useCallback(async ({ caller_id, candidate }) => {
    const pc = peersRef.current[caller_id];
    if (!pc) return;
    try { await pc.addIceCandidate(candidate); } catch {}
  }, []);

  // Open WS when enabled + token ready
  useEffect(() => {
    if (!enabled || !token) return;
    const url = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/^http/, "ws")
      + `/api/ws/radio/callin/${token}?role=host`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => {};
    ws.onmessage = async (ev) => {
      let data; try { data = JSON.parse(ev.data); } catch { return; }
      if (data.type === "joined") {
        setCallers((prev) => ({ ...prev, [data.caller_id]: prev[data.caller_id] || { stream: null } }));
      } else if (data.type === "left") {
        cleanupPeer(data.caller_id);
      } else if (data.type === "offer") {
        handleOffer(data);
      } else if (data.type === "ice") {
        handleIce(data);
      }
    };
    ws.onclose = () => { wsRef.current = null; };
    return () => {
      try { ws.close(); } catch {}
      for (const cid of Object.keys(peersRef.current)) cleanupPeer(cid);
    };
  }, [enabled, token, handleOffer, handleIce]);

  const kick = useCallback((callerId) => {
    if (wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify({ type: "kick", caller_id: callerId }));
    }
    cleanupPeer(callerId);
  }, []);

  return { callers, kick };
}
